import { IEditDocument } from "../interfaces"
import { DateTime } from "luxon"
import { FC, useEffect, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import { IDocumentCreatorProps } from "../../../../@types/documents"
import { RequestForMovementDataProvider } from '../../../../Services/DataProviders/RequestForMovementDataProvider'
import { getRequestForMovementItemEditDTO } from '../../../../libs/core-api-requests/customRequest/RequestFomMovementItemEditRequest'
import { ICostInfoDTO, IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import { IRequestForMovementCreateDTO, IRequestForMovementEditDTO, IRequestForMovementGetDTO } from "../../../../libs/coreapi-dto/documents/requestForMovement";
import { IRequestForMovementItemEditDTO, IRequestForMovementItemGetDTO } from "../../../../libs/coreapi-dto/documents/requestForMovementItem";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { RequestForMovementUpdateForm } from "./RequestForMovementUpdateForm";
import { DocumentOperationSpinner } from "../../../../components/DocumentOperationSpinner/DocumentOperationSpinner"
import { resolve } from "mathjs"
import { reject } from "lodash"

export const RequestForMovementCreatorView: FC<IDocumentCreatorProps<IRequestForMovementCreateDTO>> = (props) => {
    const appContext = useAppContext();
    const userContext = useUserContext();
    const tabsContext = useTabsContext();
    const requestForMovementDP = new RequestForMovementDataProvider(appContext.coreApiService);
    const [idGlobal, setIdGlobal] = useState(props.idGlobal);
    const [viewState, setViewState] = useState<GridStateType>(props.variant);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [editDocument, setEditDocument] = useState<IEditDocument | null>(null);

    useEffect(() => {
        const initializeComponent = async () => {
            setIsLoading(true)

            if (viewState === "edit") {
                try {
                    await new Promise((resolve, reject) => {
                        requestForMovementDP.getById(idGlobal as string, (requestForMovementDocument) => {
                            getRequestForMovementItemEditDTO(requestForMovementDocument.idGlobal as string, (e) => {
                                const documentEdit: IRequestForMovementEditDTO = {
                                    idGlobal: requestForMovementDocument.idGlobal as string,
                                    dateCreated: requestForMovementDocument.dateCreated,
                                    dateModified: requestForMovementDocument.dateModified,
                                    dateDeleted: requestForMovementDocument.dateDeleted,
                                    documentDate: requestForMovementDocument.documentDate.toF3DateTimeServerSide(userContext),
                                    idSupplierExternalGlobal: requestForMovementDocument.supplier.idGlobal as string,
                                    idSupplierStoreExternalGlobal: requestForMovementDocument.supplierStore.idGlobal as string,
                                    idContractorExternalGlobal: requestForMovementDocument.contractor.idGlobal as string,
                                    idStoreExternalGlobal: requestForMovementDocument.store.idGlobal as string,
                                    idMovementExternalGlobal: requestForMovementDocument.idMovementExternalGlobal as string,
                                    contractor: requestForMovementDocument.contractor as IEntitySimpleDTO,
                                    store: requestForMovementDocument.store as IEntitySimpleDTO,
                                    supplier: requestForMovementDocument.supplier as IEntitySimpleDTO,
                                    supplierStore: requestForMovementDocument.supplierStore as IEntitySimpleDTO,
                                    mnemocode: requestForMovementDocument.mnemocode as string,
                                    supplierCostInfo: requestForMovementDocument.supplierCostInfo as ICostInfoDTO,
                                    retailCostInfo: requestForMovementDocument.retailCostInfo as ICostInfoDTO,
                                    items: e.map(item => ({
                                        idSubstanceExternalGlobal: item.substance?.idGlobal ?? undefined,
                                        idTradeNameExternalGlobal: item.tradeName?.idGlobal ?? undefined,
                                        idCureFormExternalGlobal: item.cureForm?.idGlobal ?? undefined,
                                        dosage: item.dosage,
                                        nFv: item.nFv,
                                        quantity: item.quantity,
                                        supplierCostInfo: item.supplierCostInfo,
                                        retailCostInfo: item.retailCostInfo,
                                        substance: item.substance,
                                        tradeName: item.tradeName,
                                    })),
                                };
                                setEditDocument({
                                    requestForMovementDocument: requestForMovementDocument,
                                    documentEdit: documentEdit,
                                });
                                resolve(null)
                            });
                        });
                    })

                } catch (error) {
                    console.error('Error loading movement document:', error);
                } finally {
                    setIsLoading(false)
                }
            } else if (viewState === "copy") {
                try {
                    await new Promise((resolve, reject) => {
                        requestForMovementDP.getById(idGlobal as string, (entity) => {
                            const idGlobalNew = uuidv4();
                            const newItems = entity.items.map(item => ({
                                idGlobal: uuidv4(),
                                idSubstanceExternalGlobal: item.substance?.idGlobal ?? undefined,
                                idTradeNameExternalGlobal: item.tradeName?.idGlobal ?? undefined,
                                idCureFormExternalGlobal: item.cureForm?.idGlobal ?? undefined,
                                dosage: item.dosage,
                                nFv: item.nFv,
                                quantity: item.quantity,
                                supplierCostInfo: item.supplierCostInfo,
                                retailCostInfo: item.retailCostInfo,
                                substance: item.substance,
                                tradeName: item.tradeName,
                            })) as IRequestForMovementItemEditDTO[];

                            setEditDocument({
                                requestForMovementDocument: {
                                    idGlobal: idGlobalNew as string,
                                    documentDate: DateTime.now().toF3DateTimeServerSide(userContext),
                                    items: newItems,
                                } as IRequestForMovementGetDTO,
                                documentEdit: {
                                    idGlobal: idGlobalNew as string,
                                    idStoreExternalGlobal: entity.store.idGlobal,
                                    idContractorExternalGlobal: entity.contractor.idGlobal,
                                    idSupplierExternalGlobal: entity.supplier.idGlobal,
                                    idSupplierStoreExternalGlobal: entity.supplierStore.idGlobal,
                                    idMovementExternalGlobal: entity.idMovementExternalGlobal,
                                    documentDate: DateTime.now().toF3DateTimeServerSide(userContext),
                                    items: newItems,
                                } as IRequestForMovementEditDTO,
                            });
                            resolve(null);
                        });
                    });
                } catch (error) {
                    console.error('Error copying movement document:', error);
                } finally {
                    setIsLoading(false)
                };
            }
            else {                
                setIsLoading(false);
            }
        };
        initializeComponent()
    }, [viewState]);

    if (isLoading) {
        return (
            <div >
                <DocumentOperationSpinner xl data-testid="invoice-creator-loading" />
            </div>
        );
    }

    return (
        <>
            {viewState === "create" ? (
                <RequestForMovementUpdateForm
                    isSubmitting={isSubmitting}
                    document={null}
                    documentItems={null}
                    documentUpdate={null}
                    idGlobal={props.idGlobal}
                    variant="create"
                    save={(requestForMovement: IRequestForMovementCreateDTO) => {
                        setIsSubmitting(true);
                        requestForMovementDP.create(
                            requestForMovement,
                            (id) => {
                                setIdGlobal(id);
                                setViewState("edit");
                                props.createdCallback?.();
                                setIsSubmitting(false);
                            },
                            () => { },
                            (e) => {
                                setIsSubmitting(false);
                            }
                        );
                    }}
                    ok={(requestForMovement: IRequestForMovementCreateDTO) => {
                        setIsSubmitting(true);
                        requestForMovementDP.create(
                            requestForMovement,
                            (id) => {
                                setIdGlobal(id);
                                setViewState("create");
                                props.createdCallback?.();
                                tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                                setIsSubmitting(false);
                            },
                            () => { },
                            (e) => {
                                setIsSubmitting(false);
                            }
                        );
                    }}
                    cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                    permission={props.permission}
                />
            ) : viewState === "edit" ? (
                editDocument && (
                    <RequestForMovementUpdateForm
                        isSubmitting={isSubmitting}
                        variant="edit"
                        idGlobal={editDocument.documentEdit.idGlobal}
                        document={editDocument.requestForMovementDocument}
                        documentUpdate={editDocument.documentEdit}
                        documentItems={editDocument.requestForMovementDocument.items}
                        permission={props.permission}
                        save={(requestForMovement: IRequestForMovementEditDTO) => {
                            setIsSubmitting(true);
                            requestForMovementDP.update(idGlobal as string, requestForMovement, (e) => {
                                if (e.respType === "isFailed") return setIsSubmitting(false);
                                props.createdCallback?.();
                                setIsSubmitting(false);
                            });
                        }}
                        ok={(requestForMovement: IRequestForMovementEditDTO) => {
                            setIsSubmitting(true);
                            requestForMovementDP.update(idGlobal as string, requestForMovement, (e) => {
                                if (e.respType === "isFailed") return setIsSubmitting(false);
                                props.createdCallback?.();
                                tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                                setIsSubmitting(false);
                            });
                        }}
                        cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                    />
                )
            ) : viewState === "copy" ? (
                editDocument && (
                    <RequestForMovementUpdateForm
                        isSubmitting={isSubmitting}
                        variant="copy"
                        document={editDocument.requestForMovementDocument}
                        documentUpdate={editDocument.documentEdit}
                        documentItems={editDocument.requestForMovementDocument.items}
                        permission={props.permission}
                        save={(requestForMovement: IRequestForMovementEditDTO) => {
                            setIsSubmitting(true);
                            requestForMovementDP.create(
                                requestForMovement,
                                (id) => {
                                    setIdGlobal(id);
                                    setViewState("edit");
                                    props.createdCallback?.();
                                    setIsSubmitting(false);
                                },
                                () => { },
                                (e) => {
                                    setIsSubmitting(false);
                                }
                            );
                        }}
                        ok={(requestForMovement: IRequestForMovementEditDTO) => {
                            setIsSubmitting(true);
                            requestForMovementDP.create(
                                requestForMovement,
                                (id) => {
                                    setIdGlobal(id);
                                    setViewState("create");
                                    props.createdCallback?.();
                                    tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                                    setIsSubmitting(false);
                                },
                                () => { },
                                (e) => {
                                    setIsSubmitting(false);
                                }
                            );
                        }}
                        cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                    />
                )
            ) : (
                <></>
            )}
        </>
    );
};
